import React from 'react';
import {AnimatePresence} from '@sail/engine';
import {Divider, Icon, tokens, view, css, Action} from '@sail/ui';
import {cancel} from '@sail/icons/react/Icon';
import {message, useIntl} from '@sail/intl';
import type {DrawerConfirmationDialogProps} from '@sail/ui/deprecated';
import type {OverlayOption} from '@stripe-internal/embedded-theming';
import {useOverlayOption} from '../../context/overlayOption/OverlayOptionContext';
import {assertUnreachable} from '../../utils/typing';
import {BorderBoxContainer} from '../BorderBoxContainer/BorderBoxContainer';
import {Dialog} from './Dialog';
import {Drawer} from './Drawer';
import {InlineOverlay} from './InlineOverlay';
import {EmbeddedSupport} from '../../support';
import type {LegalLinksProps} from './OverlayStripeFooter';
import {OverlayStripeFooter} from './OverlayStripeFooter';
import {createSlots} from './utils/createSlots';
import {ViewportProvider} from '../../utils/Responsive';

const messages = {
  close: message({
    id: 'connectEmbedLib.overlay.closeButton',
    defaultMessage: 'Close',
    description: 'Button to close the Dialog component',
  }),
};

export type OverlayContentProps = {
  /** data-testid for testing purposes */
  dataTestId?: string;

  /** String describing the overlay content. This is used to help refocus when the content changes. // TODO(CONON-1929, 2024-01-01): Remove this prop */
  overlayContent?: string;

  /** Function to run when the user attempts to close the Overlay */
  handleClose: () => void;

  /** The width of the Overlay */
  size?: 'small' | 'medium' | 'large';

  /** When this is true, the Overlay uses a white background color.
   * When false, the overlay uses the background surface color design token.
   */
  hasWhiteBackground?: boolean;

  legalLinks?: LegalLinksProps;

  /** When in Dialog mode, if true, max height can exceed set 80vh */
  allowFullDialogHeight?: boolean;

  /**
   * Param to set the type of overlay
   * This param overrides the useOverlayOption return value
   */
  overlayOptionOverride?: OverlayOption;

  /**
   * When provided, shows a confirmation dialog when closing the overlay.
   * This is passed directly into the underlying Drawer/Dialog.
   */
  confirmCloseMessages?: DrawerConfirmationDialogProps['messages'];
};

export type OverlayProps = {
  /** Boolean that determines whether to render the "Contact support" button */
  showContactSupportForm?: boolean;
  supportSiteLink?: string | null;
} & OverlayContentProps;

const {Slots, Slot, SlotContent} = createSlots([
  'Step',
  'Title',
  'Subtitle',
  'Divider',
  'Footer',
]);

const OverlayWrapper: React.FC<OverlayProps> = ({
  children,
  dataTestId,
  handleClose: _handleClose,
  showContactSupportForm = false,
  size = 'medium',
  hasWhiteBackground,
  overlayContent = 'overlay',
  supportSiteLink = null,
  legalLinks,
  allowFullDialogHeight = false,
  confirmCloseMessages,
  overlayOptionOverride,
}) => {
  const intl = useIntl();
  const defaultOverlayOption = useOverlayOption();
  const overlayOption = overlayOptionOverride || defaultOverlayOption;

  // This is messy, but with the way slots work, there's no other easy way to tell if an Footer slot was provided to us
  const hasFooterSlot = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === Overlay.Footer,
  );

  const [showStackedSupportOverlay, setshowStackedSupportOverlay] =
    React.useState<boolean>(false);

  const handleClose = () => {
    // We should only close the entire overlay if the stacked support overlay is not being shown
    if (showStackedSupportOverlay) {
      setshowStackedSupportOverlay(false);
    } else {
      _handleClose();
    }
  };

  const overlayRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    setTimeout(() => {
      overlayRef.current?.focus();
    }, 250);
  }, [overlayContent]);

  const renderContent = () => (
    <Slots>
      <ViewportProvider styles={[css({stack: 'y', height: 'fill'})]}>
        <BorderBoxContainer
          styles={[
            css({
              stack: 'y',
              distribute: 'space-between',
              height: 'fill',
            }),
          ]}
        >
          {/* Header */}
          <view.div
            css={{
              stack: 'y',
              position: 'sticky',
              top: 'space.0',
              backgroundColor: 'surface',
              zIndex: 2,
              paddingX: 'space.250',
              paddingTop: 'space.250',
            }}
          >
            <Slot name="Step" />
            <view.div
              css={{
                stack: 'x',
                gap: 'space.250',
                distribute: 'space-between',
              }}
            >
              <view.div
                css={{
                  font: 'heading.medium',
                }}
              >
                <Slot name="Title" />
              </view.div>
              <Action
                aria-label={intl.formatMessage(messages.close)}
                onPress={handleClose}
                type="icon"
                css={{
                  alignSelfY: 'top',
                  width: 'fit',
                }}
                data-testid="overlay-close-button"
              >
                <Icon icon={cancel} />
              </Action>
            </view.div>
            <Slot name="Subtitle" />
            <Slot name="Divider" />
          </view.div>

          {/* Content */}
          <view.div
            css={{
              stack: 'y',
              height: 'fill',
              gap: 'space.250',
              distribute: 'space-between',
              padding: 'space.250',
              overflowY: 'auto',
            }}
          >
            <view.div
              css={{
                stack: 'y',
                gap: 'space.250',
                zIndex: 1,
              }}
            >
              {children}
            </view.div>
            {!hasFooterSlot && (
              <OverlayStripeFooter
                legalLinks={legalLinks}
                showContactSupportForm={showContactSupportForm}
                showSupportOverlay={showStackedSupportOverlay}
                supportSiteLink={supportSiteLink}
                handleShowSupport={() => {
                  setshowStackedSupportOverlay(true);
                }}
              />
            )}
          </view.div>
          {/* Action Section */}
          {hasFooterSlot && (
            <view.div
              css={{
                backgroundColor: 'surface',
                borderTop: '1px solid',
                borderColor: 'border',
                stack: 'y',
                gap: 'space.250',
                position: 'sticky',
                bottom: 'space.0',
                zIndex: 2,
                padding: 'space.250',
              }}
            >
              <Slot name="Footer" />
              <OverlayStripeFooter
                legalLinks={legalLinks}
                showContactSupportForm={showContactSupportForm}
                showSupportOverlay={showStackedSupportOverlay}
                supportSiteLink={supportSiteLink}
                handleShowSupport={() => {
                  setshowStackedSupportOverlay(true);
                }}
              />
            </view.div>
          )}
        </BorderBoxContainer>
      </ViewportProvider>
    </Slots>
  );

  const renderEmbeddedSupport = () => (
    <AnimatePresence>
      {showStackedSupportOverlay && (
        <Overlay
          dataTestId="embedded-support"
          handleClose={handleClose}
          size={size}
          hasWhiteBackground={hasWhiteBackground}
          overlayOptionOverride={overlayOptionOverride}
        >
          <EmbeddedSupport />
        </Overlay>
      )}
    </AnimatePresence>
  );

  const renderDialog = () => (
    <Dialog
      dataTestId={dataTestId}
      handleClose={handleClose}
      size={size}
      hasWhiteBackground={hasWhiteBackground}
      allowFullDialogHeight={allowFullDialogHeight}
      confirmCloseMessages={confirmCloseMessages}
    >
      <view.div tabIndex={-1} ref={overlayRef} css={{focusRing: 'none'}}>
        {renderContent()}
        {renderEmbeddedSupport()}
      </view.div>
    </Dialog>
  );

  const renderDrawer = () => (
    <Drawer
      dataTestId={dataTestId}
      handleClose={handleClose}
      size={size}
      hasWhiteBackground={hasWhiteBackground}
      confirmCloseMessages={confirmCloseMessages}
    >
      <view.div
        tabIndex={-1}
        ref={overlayRef}
        css={{focusRing: 'none', height: 'fill'}}
      >
        {renderContent()}
        {renderEmbeddedSupport()}
      </view.div>
    </Drawer>
  );

  const renderInline = () => (
    <InlineOverlay dataTestId={dataTestId}>
      {renderContent()}
      {renderEmbeddedSupport()}
    </InlineOverlay>
  );

  switch (overlayOption) {
    case 'drawer':
      return renderDrawer();
    case 'dialog':
      return renderDialog();
    case 'inline':
      return renderInline();
    default:
      assertUnreachable(overlayOption);
      return renderDialog();
  }
};

const OverlayStep: React.FC = ({children}) => (
  <SlotContent name="Step">{children}</SlotContent>
);

const OverlayTitle: React.FC = ({children}) => (
  <>
    <SlotContent name="Title">
      <view.div css={{color: 'text'}}>{children}</view.div>
    </SlotContent>
    <OverlayDivider />
  </>
);

type SpacingTokens =
  | 'space.50'
  | 'space.75'
  | 'space.100'
  | 'space.150'
  | 'space.200'
  | 'space.250';

const OverlaySubtitle: React.FC<{
  marginTop?: SpacingTokens;
}> = ({children, marginTop}) => (
  <SlotContent name="Subtitle">
    <view.div css={{font: 'body.small', color: 'subdued', marginTop}}>
      {children}
    </view.div>
  </SlotContent>
);

const OverlayDivider: () => JSX.Element = () => (
  <SlotContent name="Divider">
    <Divider
      css={{
        marginTop: 'space.250',
        width: `calc(100% + ${tokens.space[500]})`,
        marginLeft: `calc(-1 * ${tokens.space[250]})`,
      }}
    />
  </SlotContent>
);

const OverlayFooter: React.FC = ({children}) => (
  <SlotContent name="Footer">{children}</SlotContent>
);

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const Overlay = Object.assign(OverlayWrapper, {
  Step: OverlayStep,
  Title: OverlayTitle,
  Subtitle: OverlaySubtitle,
  Footer: OverlayFooter,
});
